import { useState, useEffect } from 'react';
import axios from 'axios';
import 'react-loading-skeleton/dist/skeleton.css';
import TableSkeleton from './TableSkeleton';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';

const formatDateShort = (dateString) => {
    if (!dateString) return "N/A";
    try {
        return new Date(dateString).toLocaleDateString('en-CA');
    } catch (e) {
        return "Invalid Date";
    }
};

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const BASE_URL = "https://api.muktihospital.com/api";

const MyPatientsTable = ({ user }) => {
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const totalRecords = patients.length;
    const totalPages = Math.ceil(totalRecords / itemsPerPage) || 1;
    const validCurrentPage = Math.min(Math.max(1, currentPage), totalPages);

    const indexOfLastItem = validCurrentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentPatients = patients.slice(indexOfFirstItem, indexOfLastItem);

    useEffect(() => {
        if (!user?.email) {
            setLoading(false);
            console.log("MyPatientsTable: Waiting for user email...");
            return;
        }

        const fetchMyPatients = async () => {
            setLoading(true);
            try {
                const api = axios.create({ baseURL: BASE_URL, headers: { "x-api-key": API_KEY } });
                const doctorsRes = await api.get(`/doctor`);

                const fetchedDoctors = doctorsRes.data?.data || doctorsRes.data || [];
                const userEmail = user.email;
                const myDoctorProfile = fetchedDoctors.find(doc => doc.email === userEmail);

                if (!myDoctorProfile) {
                    console.error(`MyPatientsTable: NO DOCTOR PROFILE FOUND for email: ${userEmail}. Check if this email exists in the fetched doctors list.`);
                    setLoading(false);
                    return;
                }

                const loggedInDoctorId = myDoctorProfile.id;
                const appointmentsRes = await api.get(`/appointment`);
                const allAppointments = appointmentsRes.data?.appointments || [];

                const myAppointments = allAppointments.filter(app => app.doctorId === loggedInDoctorId);
                const patientMap = new Map();
                myAppointments.sort((a, b) =>
                    new Date(b.appointmentDate || b.createdAt) - new Date(a.appointmentDate || a.createdAt)
                );

                myAppointments.forEach(appt => {

                    if (!patientMap.has(appt.patientId)) {
                        console.log("Adding patient to map:", appt.patientId, "Name:", appt.patientName || appt.patient?.name);
                        patientMap.set(appt.patientId, {
                            id: appt.patientId,
                            name: appt.patientName || appt.patient?.name || 'N/A',
                            phoneNumber: appt.mobileNumber || appt.patient?.phoneNumber || 'N/A',
                            age: appt.age != null ? appt.age : appt.patient?.age,
                            gender: appt.patient?.gender || 'N/A',
                            bloodGroup: appt.bloodGroup || appt.patient?.bloodGroup || 'N/A',
                            lastVisit: appt.appointmentDate || appt.createdAt,
                            email: appt.patient?.email || 'N/A'
                        });
                    } else {
                         console.log("Patient already in map, skipping:", appt.patientId);
                    }
                });


                setPatients(Array.from(patientMap.values()));

            } catch (error) {
                console.error("Error fetching patient data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchMyPatients();

    }, [user]);

    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const skeletonColumns = [
        { width: '150px', height: '16px', className: 'py-5 px-3' },
        { width: '120px', height: '16px', className: 'py-5 px-3' }, 
        { width: '180px', height: '16px', className: 'py-5 px-3' }, 
        { width: '60px', height: '16px', className: 'py-5 px-3' }, 
        { width: '80px', height: '16px', className: 'py-5 px-3' },
        { width: '100px', height: '16px', className: 'py-5 px-3' },
    ];

    return (
        <div className="bg-white shadow rounded-lg mt-6">
            <div className="border-b border-dashed border-M-text-color/50 p-4 overflow-hidden">
                 <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">Patients List</h2>
            </div>
            <div className="overflow-x-auto">
                    <table className="min-w-full w-full divide-y divide-gray-200">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Patient Name</th>
                                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Contact</th>
                                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Email</th>
                                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Age</th>
                                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Gender</th>
                                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Last Visit</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {loading ? (
                            <TableSkeleton columns={skeletonColumns} rowCount={itemsPerPage} />
                        ) : currentPatients.length > 0 ? (
                            currentPatients.map((patient) => (
                                <tr key={patient.id} className="hover:bg-gray-50">
                                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">{patient.name}</td>
                                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">{patient.phoneNumber}</td>
                                    <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">{patient.email ?? 'N/A'}</td>
                                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">{patient.age ?? 'N/A'}</td>
                                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">{patient.gender?.toLowerCase() || 'N/A'}</td>
                                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">{formatDateShort(patient.lastVisit)}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" className="text-center py-5 text-gray-500">
                                    You haven't seen any patients yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                    </table>
            </div>
            {totalPages > 1 && !loading && (
                <div className="p-5">
                    <div className="flex items-center justify-between w-full flex-wrap gap-y-3">
                        <p className="text-sm text-gray-600 text-left">
                            Records: {indexOfFirstItem + 1} to{' '}
                            {Math.min(indexOfLastItem, totalRecords)} of {totalRecords}
                        </p>
                        <nav aria-label="Pagination">
                            <ul className="inline-flex items-center justify-end border border-gray-300 rounded-md overflow-hidden">
                                <li>
                                    <button
                                        className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed border-r border-gray-300" // Added border-r
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        <MdKeyboardArrowLeft size={18} /> 
                                    </button>
                                </li>
                                {(() => {
                                    const pageNumbers = [];
                                    const maxVisiblePages = 7; 

                                    if (totalPages <= maxVisiblePages) {
                                        // Show all pages
                                        for (let i = 1; i <= totalPages; i++) {
                                            pageNumbers.push(i);
                                        }
                                    } else {
                                        // Show ellipsis logic
                                        let startPage = Math.max(1, currentPage - 2);
                                        let endPage = Math.min(totalPages, currentPage + 2);

                                        if (currentPage <= 3) { endPage = maxVisiblePages - 2; startPage = 1;}
                                        if (currentPage > totalPages - 3) { startPage = totalPages - (maxVisiblePages - 3); endPage = totalPages;}

                                        pageNumbers.push(1);
                                        if (startPage > 2) pageNumbers.push('...');

                                        for (let i = startPage; i <= endPage; i++) {
                                            if (i !== 1 && i !== totalPages) {
                                                 pageNumbers.push(i);
                                            }
                                        }

                                        if (endPage < totalPages - 1) pageNumbers.push('...');
                                        pageNumbers.push(totalPages);
                                    }

                                    return pageNumbers.map((page, index) => (
                                        <li key={index}>
                                            {page === '...' ? (
                                                <span className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border-r border-gray-300">...</span>
                                            ) : (
                                                <button
                                                    className={`relative inline-flex items-center px-4 py-2 text-sm font-medium border-r border-gray-300 ${
                                                        currentPage === page
                                                            ? "bg-blue-600 text-white hover:bg-blue-700" // Active page style (adjust color)
                                                            : "bg-white text-gray-700 hover:bg-gray-50" // Default page style
                                                    }`}
                                                    onClick={() => handlePageChange(page)}
                                                >
                                                    {page}
                                                </button>
                                            )}
                                        </li>
                                    ));
                                })()}

                                {/* Next Button */}
                                <li>
                                    <button
                                        className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" // Removed border-r on last item
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    >
                                        <MdKeyboardArrowRight size={18} />
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyPatientsTable;